import { useContext, useMemo } from "react";
import { NextPage } from "next";
import { Box, Flex, Text, Divider, Container } from "@chakra-ui/react";

// Components
import { PageContext, Footer, Header, useStaderTVL } from "mlp-sdk";

// Providers
import { PageProvider } from "providers/PageProvider";

// Utils
import { prepareNavData } from "mlp-sdk/utils/common";

import navData from "@constants/navData.json";
import privacyPolicyPageData from "@constants/privacyPolicyPage.json";
import PageHead from "@components/PageHead/PageHead";

const PrivacyPolicy = () => {
  const { page } = useContext(PageContext);

  const _pageData = page.data.pageData;

  const pageData = _pageData
    ? _pageData.private_policy_data?.tos_details?.reduce(
        (acc: any, curr: any) =>
          acc.set(curr.title.toLowerCase().replaceAll(" ", "-"), curr),
        new Map()
      )
    : new Map();

  const items = pageData ? Array.from(pageData.entries()) : [];
  const sidebarMenus = items.map((item: any) => item[0]);

  const { tvl } = useStaderTVL();

  const { navData, networks } = useMemo(
    () => prepareNavData(page.data.navData, tvl),
    [page.data.navData, tvl]
  );

  return (
    <>
      <PageHead page="privacy-policy" />
      <Header data={navData} networks={networks} tvl={tvl} />
      <Container px={{ base: 4, xl: "inherit" }} mt="56px">
        <Text
          mb={{ base: 4, lg: 12 }}
          textStyle={{ base: "h2", lg: "h1" }}
          textAlign="center"
        >
          {_pageData.private_policy_data.section_details?.title}
        </Text>

        <Flex
          w="100%"
          gap="85px"
          justifyContent="space-between"
          textAlign="left"
        >
          <Box mt={{ base: 8, lg: 0 }}>
            {sidebarMenus?.map((menu: string) => {
              const description = pageData.get(menu).description;
              return (
                <Box id={menu.toLowerCase().replaceAll(" ", "-")} key={menu}>
                  <Text fontWeight="700" fontSize="24px" lineHeight="28px">
                    {pageData.get(menu).title}
                  </Text>
                  <Box
                    key={description}
                    mt={6}
                    textStyle={{ base: "p12", lg: "p16" }}
                    fontWeight={500}
                    color="textSecondary"
                    dangerouslySetInnerHTML={{ __html: description }}
                    sx={{
                      "& ol": {
                        pl: 4,
                      },
                      "& ul": {
                        pl: 4,
                      },
                      "& table": {
                        border: "1px solid black",
                      },
                      "& td": {
                        border: "1px solid black",
                        p: "4px",
                      },
                      "@media screen and (max-width: 500px)": {
                        "& figure": {
                          width: "calc(100vw - 32px)",
                          overflow: "auto",
                          maxWidth: "100%",
                        },
                      },
                    }}
                  />
                  <Divider my="32px" color="divider" />
                </Box>
              );
            })}
          </Box>
        </Flex>
      </Container>
      <Footer data={navData} />
    </>
  );
};

const PrivacyPolicyPage: NextPage = ({ page }: any) => {
  return (
    <PageProvider page={page}>
      <PrivacyPolicy />
    </PageProvider>
  );
};

export async function getStaticProps() {
  return {
    props: {
      page: {
        data: {
          navData,
          pageData: privacyPolicyPageData,
        },
        error: { navError: null, pageError: null },
      },
    },
  };
}

export default PrivacyPolicyPage;
